import axios from 'axios';
import { Form, getIn, useFormikContext } from 'formik';
import { useEffect, useMemo, useState } from 'react';

import { FormikAgentSelectFlow } from '../../../agent/AgentSelectFlow/FormikAgentSelectFlow';
import { Button } from '../../../cdl/Button/Button';
import { Callout } from '../../../cdl/Callout/Callout';
import { DropdownMenu } from '../../../cdl/DropdownMenu/DropdownMenu';
import { FormikInput } from '../../../cdl/Input/FormikInput';
import { SegmentedControl } from '../../../cdl/SegmentedControl/SegmentedControl';
import { Switch } from '../../../cdl/Switch/Switch';
import { Tag } from '../../../cdl/Tag/Tag';
import { Text } from '../../../cdl/Text/Text';
import { FormikTextArea } from '../../../cdl/TextArea/FormikTextArea';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { ContentLoadingOverlay } from '../../../common/ContentLoadingOverlay/ContentLoadingOverlay';
import { CreationComment } from '../../../common/CreationComment/CreationComment';
import { Divider } from '../../../common/Divider/Divider';
import { FormikDatePicker } from '../../../common/form-elements/formik/FormikDatePicker';
import { FormikDebug } from '../../../common/form-elements/formik/FormikDebug';
import { useFormikDirtyWarning } from '../../../common/form-elements/formik/useFormikDirtyWarning';
import { FormikProductTable } from '../../../common/FormikProductTable/FormikProductTable';
import { startOfTimeZoneDayAsUtcIso } from '../../../common/helpers/dateFns.helper';
import { getOrderSourceHumanReadableValue } from '../../../common/helpers/getOrderSourceHumanReadableValue.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { useSchedules } from '../../../common/hooks/useSchedules';
import { useVesselProducts } from '../../../common/hooks/useVesselProducts';
import { useVesselProductUpdates } from '../../../common/hooks/useVesselProductUpdates';
import { IconAlertTriangle } from '../../../common/icons/cdl/AlertTriangle';
import { IconInfoCircle } from '../../../common/icons/cdl/InfoCircle';
import { useFlags } from '../../../common/launchDarkly/useFlags';
import { OverlayHeader } from '../../../common/Overlay/OverlayHeader';
import { Box } from '../../../common/ui/Box';
import { theme } from '../../../common/ui/theme';
import { useCustomerGroupSettings } from '../../../group/customer/detail/hooks/useCustomerGroupSettings';
import { FormikPortDetailSelect } from '../../../port/portSelect/FormikPortDetailSelect';
import { FormikVesselSelect } from '../../../vessel/FormikVesselSelect';
import { DeleteDraftConfirmPopup } from '../../common/DeleteDraftConfirmPopup';
import { OrderVesselInformation } from '../../create/OrderVesselInformation';
import { FormikCompareOverlay } from '../../create-enquiry/components/FormikCompareOverlay';
import { FormikSupplierSelect } from '../../create-enquiry/components/FormikSupplierSelect';
import { SuggestionButton } from '../../create-enquiry/components/SuggestionButton';
import { SuggestionData } from '../../create-enquiry/components/SuggestionDrawer';
import { ViewVesselProductsModal } from '../../create-enquiry/components/ViewVesselProductsModal';
import { FormikCreateEnquiryValues } from '../../create-enquiry/CreateEnquiryFlow';
import { useDraftChanges } from '../../create-enquiry/hooks/useDraftChanges';
import { useDraftCreate } from '../../create-enquiry/hooks/useDraftCreate';
import { useEnquiryOrderMode } from '../../create-enquiry/hooks/useEnquiryOrderMode';
import { useEnquiryUpdate } from '../../create-enquiry/hooks/useEnquiryUpdate';
import { OrderMode } from '../../create-enquiry/types/OrderMode';
import { buildOrderRequest } from '../../create-enquiry/util/buildOrderRequest';
import { getSupplierSubtextTranslationKey } from '../../create-enquiry/util/getSupplierSubtextTranslationKey';
import { OrderState } from '../../model/OrderModel';

interface CreateEnquiryFormProps {
    onCloseClick: (dirty: boolean) => void;
    orderId?: string;
    orderSource?: string;
    onDeleteDraft?: () => void;
    isDeletingDraft?: boolean;
    draftDeleteReference?: string;
    isPlacingDirectOrder?: boolean;
    isConfirmingDirectOrder?: boolean;
}

export const FormikCreateEnquiryForm = ({
    onCloseClick,
    orderId,
    orderSource,
    onDeleteDraft,
    isDeletingDraft = false,
    draftDeleteReference,
    isPlacingDirectOrder = false,
    isConfirmingDirectOrder = false,
}: CreateEnquiryFormProps) => {
    useFormikDirtyWarning();
    const [isViewProductsModalOpen, setIsViewProductsModalOpen] = useState(false);
    const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
    const useDraftCreateMutation = useDraftCreate();
    const useEnquiryUpdateMutation = useEnquiryUpdate();

    const { addToast, addErrorToast } = useToasts();
    const {
        initialValues,
        values,
        isSubmitting,
        setSubmitting,
        setFieldError,
        setFieldValue,
        dirty,
        errors,
        touched,
        submitCount,
        submitForm,
    } = useFormikContext<FormikCreateEnquiryValues>();
    const role = useRole();
    const {
        selectedVessel,
        canShowDirectOrder,
        isCheckingDirectOrderEligibility,
        isDirectOrder,
        handleOrderModeChange,
    } = useEnquiryOrderMode({ isPlacingDirectOrder, isConfirmingDirectOrder });

    const duplicateItemsError = getIn(errors, 'duplicateItemsError');
    const showDuplicateItemsError = Boolean(duplicateItemsError && (submitCount > 0 || touched?.items));

    const { hasUnsavedChanges, hasSuggestionRelevantChanges, setLastSavedValues, setLastSuggestionValues } =
        useDraftChanges(initialValues);

    const { schedules: schedule } = useSchedules({ imo: selectedVessel?.imo });
    const customerGroupSettingsQuery = useCustomerGroupSettings(role.group.id);

    const showScheduleSharingToggle = useMemo(() => {
        const settings = customerGroupSettingsQuery.data;
        if (!settings?.scheduleSharingEnabled) return false;
        if (!settings.scheduleSharingExcludeEnabled) return true;
        const hasExclusions = settings.scheduleSharingExcludedCompanyIds?.length > 0;
        if (!hasExclusions) return true;
        if (!values.customerId) return false;
        return !settings.scheduleSharingExcludedCompanyIds.includes(values.customerId);
    }, [customerGroupSettingsQuery.data, values.customerId]);

    useEffect(() => {
        if (showScheduleSharingToggle) {
            if (values.scheduleSharingEnabled === undefined) {
                setFieldValue('scheduleSharingEnabled', true);
            }
        } else {
            setFieldValue('scheduleSharingEnabled', undefined);
        }
    }, [showScheduleSharingToggle, setFieldValue, values.scheduleSharingEnabled]);

    const flags = useFlags();

    const isDraftEnquiry = !!orderId;
    const humanReadableOrderSource = getOrderSourceHumanReadableValue(orderSource);
    const supplierSubtextTranslationKey = getSupplierSubtextTranslationKey(selectedVessel, (customerId) =>
        role.isSpotAllowed(customerId)
    );

    useVesselProductUpdates(values.vesselId, selectedVessel?.customerId);
    const vesselProductsQuery = useVesselProducts(values.vesselId);

    const productsNotInCatalog = useMemo(() => {
        if (!vesselProductsQuery.data?.products?.length || !values.items?.length) {
            return [];
        }

        const vesselProductIds = new Set(
            vesselProductsQuery.data.products.map((vesselProduct) => vesselProduct.productId)
        );
        const uniqueProductsNotInCatalog = new Set<string>();

        values.items.forEach((item) => {
            if (!item.productId || item.deleted || vesselProductIds.has(item.productId) || !item.product?.name) {
                return;
            }

            uniqueProductsNotInCatalog.add(item.product.name);
        });

        return Array.from(uniqueProductsNotInCatalog);
    }, [vesselProductsQuery.data?.products, values.items]);

    const hasUnresolvedSourceValueRows = useMemo(() => {
        return values.items?.some((item) => !item.deleted && !item.productId && item.sourceValues?.product?.name);
    }, [values.items]);

    const canRenderSupplierSelection = !isDraftEnquiry || !!selectedVessel;

    const handleDraftSave = async (values: FormikCreateEnquiryValues) => {
        if (orderId) {
            await useEnquiryUpdateMutation.mutateAsync(
                { order: buildOrderRequest(values, OrderState.DRAFT), orderId },
                {
                    onSuccess: () => {
                        addToast(translate('order.successDraftToast'));
                        setLastSavedValues(values);
                    },
                    onError: (error) => {
                        if (axios.isAxiosError(error) && error.response?.status === 400) {
                            addErrorToast(translate('order.validationFailedToast'));
                            return;
                        }
                        addErrorToast(translate('order.errorDraftToast'));
                    },
                }
            );
            return;
        }

        await useDraftCreateMutation.mutateAsync(buildOrderRequest(values, OrderState.DRAFT));
        setLastSavedValues(values);
    };

    const updateFormWithSuggestionData = (suggestionData: SuggestionData) => {
        setFieldValue('orderMode', OrderMode.ENQUIRY, false);
        setFieldValue('portId', suggestionData.portId, false);
        setFieldValue('dateDelivery', suggestionData.dateDelivery, false);
        setFieldValue('receiverSupplierIds', suggestionData.receiverSupplierIds, false);
        setFieldValue('spot', suggestionData.spot, false);
    };

    const handleSuggestionSaveDraft = async (suggestionData?: SuggestionData) => {
        if (suggestionData) {
            updateFormWithSuggestionData(suggestionData);
            await handleDraftSave({ ...values, ...suggestionData });
        } else {
            await handleDraftSave(values);
        }
    };

    const handleSuggestionSendEnquiry = async (suggestionData: SuggestionData) => {
        updateFormWithSuggestionData(suggestionData);
        setSubmitting(true);

        // Wait for the form to update before submitting
        setTimeout(() => {
            submitForm();
        }, 100);
    };

    const deleteDraftAction = onDeleteDraft
        ? {
              label: translate('actionListLabel.delete'),
              destructive: true,
              onSelect: () => setShowDeleteConfirmPopup(true),
          }
        : null;

    return (
        <Form>
            <OverlayHeader
                zIndex={theme.zIndex.overlay - 1}
                title={
                    <Box display="flex" alignItems="center" gap={8}>
                        <Text variant="title">
                            {isDraftEnquiry ? translate('order.createNewDraft') : translate('order.createnew')}
                        </Text>
                        {isDraftEnquiry && <Tag variant="default">{translate('order.draft')}</Tag>}
                        {humanReadableOrderSource ? <Tag variant="accent">{humanReadableOrderSource}</Tag> : null}
                    </Box>
                }
                loadingOverlay={
                    <ContentLoadingOverlay
                        isLoading={isPlacingDirectOrder}
                        label={translate('order.directOrder.placingOrder')}
                    />
                }
                isCloseDisabled={isPlacingDirectOrder}
                onClose={() => {
                    if (!isPlacingDirectOrder) {
                        onCloseClick(dirty);
                    }
                }}
                trailingVisual={
                    <Box
                        display="flex"
                        justifyContent="right"
                        alignItems="center"
                        gap={8}
                        inert={isPlacingDirectOrder || isSubmitting}
                    >
                        {orderId && flags.closelinkIntelligence ? (
                            <SuggestionButton
                                orderId={orderId}
                                onSaveDraft={handleSuggestionSaveDraft}
                                onSendEnquiry={handleSuggestionSendEnquiry}
                                onSuggestionFinished={() => setLastSuggestionValues(values)}
                                hasUnsavedChanges={hasUnsavedChanges}
                                hasSuggestionRelevantChanges={hasSuggestionRelevantChanges}
                            />
                        ) : null}

                        {values.customerId && !isDraftEnquiry ? (
                            <Button
                                type="button"
                                isLoading={useDraftCreateMutation.isPending}
                                disabled={
                                    !hasUnsavedChanges ||
                                    isPlacingDirectOrder ||
                                    isConfirmingDirectOrder ||
                                    isSubmitting
                                }
                                onClick={() => handleDraftSave(values)}
                            >
                                {translate('order.saveAsDraftButton')}
                            </Button>
                        ) : null}

                        {isDraftEnquiry ? (
                            <Button
                                type="button"
                                isLoading={useEnquiryUpdateMutation.isPending}
                                disabled={
                                    !hasUnsavedChanges ||
                                    isPlacingDirectOrder ||
                                    isConfirmingDirectOrder ||
                                    isSubmitting
                                }
                                onClick={() => handleDraftSave(values)}
                            >
                                {translate('order.saveDraftButton')}
                            </Button>
                        ) : null}

                        <Button
                            type="submit"
                            emphasis="high"
                            isLoading={isSubmitting || isPlacingDirectOrder}
                            disabled={isDirectOrder && !canShowDirectOrder}
                        >
                            {isDirectOrder
                                ? translate('order.directOrder.placeDirectOrder')
                                : translate('order.btncreate')}
                        </Button>

                        {deleteDraftAction ? <DropdownMenu options={[deleteDraftAction]} align="end" /> : null}
                    </Box>
                }
            >
                <Box
                    padding={32}
                    display="flex"
                    flexDirection="column"
                    gap={32}
                    inert={isPlacingDirectOrder || isSubmitting}
                >
                    {values.creationComment ? (
                        <Box width="50%" paddingRight="20px">
                            <CreationComment text={values.creationComment} />
                        </Box>
                    ) : null}

                    <div>
                        <Text variant="title">{translate('order.coreData')}</Text>
                        <Text variant="body" color="foreground.muted" as="p">
                            {translate('order.coreDataSubText')}
                        </Text>

                        <Divider marginTop={16} marginBottom={32} />
                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={40} marginBottom={24}>
                            <Box display="flex" gap={16} flexDirection="column">
                                <FormikVesselSelect
                                    name="vesselId"
                                    label={translate('global.coreData.vessel')}
                                    customerIds={role.getCompanies().map((company) => company.id)}
                                    onVesselSelect={(vessel) => {
                                        setFieldValue('orderMode', OrderMode.ENQUIRY, false);
                                        setFieldValue('customerId', vessel?.customerId);
                                    }}
                                />

                                <FormikPortDetailSelect
                                    name="portId"
                                    schedule={schedule}
                                    vesselId={values.vesselId}
                                    label={translate('order.port')}
                                    onSelect={(port, scheduleRank) => {
                                        setFieldValue('agentId', undefined, false);

                                        const matchingSchedule = schedule.find(
                                            (schedule) =>
                                                schedule.port?.id === port.id && schedule.rank === scheduleRank
                                        );

                                        if (matchingSchedule) {
                                            setFieldValue(
                                                'dateDelivery',
                                                // Set dateDelivery to the schedules current date, but at the start of the day in UTC.
                                                // With the format we remove the timezone of the schedule.
                                                startOfTimeZoneDayAsUtcIso(
                                                    matchingSchedule.eta,
                                                    matchingSchedule.timeZoneId
                                                ),
                                                false
                                            );
                                            setFieldError('dateDelivery', undefined);

                                            if (matchingSchedule.agentIds?.length === 1) {
                                                setFieldValue('agentId', matchingSchedule.agentIds[0], false);
                                            }
                                        }
                                    }}
                                />

                                <FormikDatePicker name="dateDelivery" label={translate('order.dateDelivery')} />

                                {values.customerId && (values.portId || isDraftEnquiry) ? (
                                    <FormikInput
                                        label={translate('order.buyerReferenceOptional')}
                                        name="buyerReference"
                                        markAsOptional
                                    />
                                ) : null}

                                {values.customerId && values.portId ? (
                                    <FormikAgentSelectFlow
                                        name="agentId"
                                        label={translate('order.agentOptional')}
                                        customerId={selectedVessel?.customerId}
                                        portId={values.portId}
                                        markAsOptional
                                    />
                                ) : null}
                                <FormikTextArea
                                    name="comment"
                                    label={translate('order.commentForSupplier')}
                                    caption={translate('order.commentForSupplierCaption')}
                                    rows={4}
                                    markAsOptional
                                />
                            </Box>

                            <div>
                                <OrderVesselInformation vesselId={values.vesselId} />
                            </div>
                        </Box>
                    </div>

                    <div>
                        <Text variant="title">{translate('order.products')}</Text>
                        <Text variant="body" color="foreground.muted" as="p">
                            {translate('order.productssubtext')}
                        </Text>

                        <Divider marginTop={16} marginBottom={32} />
                        {hasUnresolvedSourceValueRows ? (
                            <Box marginBottom={16}>
                                <Callout
                                    variant="attention"
                                    heading={translate('order.lubes.unmappedProducts.heading')}
                                    description={translate('order.lubes.unmappedProducts.description')}
                                    Icon={IconAlertTriangle}
                                />
                            </Box>
                        ) : null}
                        <Box>
                            <FormikProductTable
                                config={{
                                    initialItems: [],
                                    editable: true,
                                    showPrices: false,
                                    editSurcharges: false,
                                    editPrices: false,
                                }}
                            />
                        </Box>
                        {showDuplicateItemsError ? (
                            <Box marginTop={16}>
                                <Callout variant="negative" description={duplicateItemsError} Icon={IconInfoCircle} />
                            </Box>
                        ) : null}

                        {productsNotInCatalog.length > 0 ? (
                            <Box marginTop={16} marginBottom={32}>
                                <Callout
                                    heading={translate('order.lubes.productsNotInCatalog.heading')}
                                    description={translate('order.lubes.productsNotInCatalog.description', {
                                        products: productsNotInCatalog.join(', '),
                                    })}
                                    Icon={IconInfoCircle}
                                >
                                    <Button type="button" onClick={() => setIsViewProductsModalOpen(true)}>
                                        {translate('order.lubes.viewProducts.button')}
                                    </Button>
                                </Callout>
                            </Box>
                        ) : null}
                    </div>

                    <Box display="flex" flexDirection="column" gap={24}>
                        <Box display="flex" flexDirection="column">
                            <Text variant="title">{translate('order.supplier')}</Text>
                            <Text variant="body" color="foreground.muted" as="p">
                                {isDirectOrder
                                    ? translate('order.directOrder.selectSupplier')
                                    : translate(supplierSubtextTranslationKey)}
                            </Text>
                        </Box>

                        {canShowDirectOrder ? (
                            <Box
                                width="fit-content"
                                inert={isCheckingDirectOrderEligibility || isConfirmingDirectOrder}
                            >
                                <SegmentedControl
                                    options={[
                                        {
                                            value: OrderMode.ENQUIRY,
                                            label: translate('order.directOrder.enquiry'),
                                        },
                                        {
                                            value: OrderMode.DIRECT_ORDER,
                                            label: translate('order.directOrder.directOrder'),
                                        },
                                    ]}
                                    value={values.orderMode}
                                    onChange={handleOrderModeChange}
                                />
                            </Box>
                        ) : null}

                        <FormikCompareOverlay
                            defaultContractId={selectedVessel?.defaultContractId}
                            schedule={schedule}
                        />

                        {canRenderSupplierSelection ? (
                            <FormikSupplierSelect
                                portId={values.portId}
                                customerId={selectedVessel?.customerId}
                                customerGroupId={selectedVessel?.customer?.customerGroupId}
                                vesselId={selectedVessel?.id}
                                defaultSupplierId={selectedVessel?.defaultSupplierId}
                                defaultContractId={selectedVessel?.defaultContractId}
                            />
                        ) : null}
                    </Box>

                    {showScheduleSharingToggle ? (
                        <Box>
                            <Switch
                                checked={values.scheduleSharingEnabled !== false}
                                onChange={(checked) => setFieldValue('scheduleSharingEnabled', checked)}
                                label={translate('order.scheduleSharingLabel')}
                                description={translate('order.scheduleSharingDescription')}
                            />
                        </Box>
                    ) : null}
                </Box>

                <FormikDebug />
            </OverlayHeader>

            {selectedVessel && vesselProductsQuery.data?.products.length ? (
                <ViewVesselProductsModal
                    isOpen={isViewProductsModalOpen}
                    onDismiss={() => setIsViewProductsModalOpen(false)}
                    vessel={selectedVessel}
                    vesselProducts={vesselProductsQuery.data.products}
                />
            ) : null}
            <DeleteDraftConfirmPopup
                isOpen={showDeleteConfirmPopup}
                onSubmit={() => onDeleteDraft?.()}
                onDismiss={() => setShowDeleteConfirmPopup(false)}
                reference={draftDeleteReference}
                isSubmitting={isDeletingDraft}
            />
        </Form>
    );
};
